import { AnalystHeatmapCategoryKind } from 'src/app/enums/analystHeatmapCategoryKind.enum';
import { AnalystChart } from 'src/app/models/analyst-output.model';

export interface SharedTooltipEntry {
  name: string;
  value: number | null | undefined;
}

export const ANALYST_CHART_BASE_HEIGHT = 260;
export const LEGEND_ROW_HEIGHT = 24;
export const LEGEND_MAX_ROWS = 10;
export const LEGEND_MAX_HEIGHT = LEGEND_MAX_ROWS * LEGEND_ROW_HEIGHT;
export const HEATMAP_ROW_HEIGHT = 24;
export const HEATMAP_AXIS_ALLOWANCE = 70;

const TOOLTIP_FRACTION_DIGITS = 5;
const TOOLTIP_NUMBER_LOCALE = 'en-US';
const DEFAULT_LABEL_LOCALE = 'en';
const CHART_MARKUP_CHARACTERS = /[[\]{}]/g;

export function escapeChartMarkup(text: string): string {
  return text.replace(CHART_MARKUP_CHARACTERS, (character) => character + character);
}

export function formatTooltipNumber(value: number): string {
  return value.toLocaleString(TOOLTIP_NUMBER_LOCALE, { maximumFractionDigits: TOOLTIP_FRACTION_DIGITS });
}

export function buildSharedTooltipText(entries: SharedTooltipEntry[]): string {
  return entries
    .filter((entry): entry is { name: string; value: number } => entry.value !== null && entry.value !== undefined)
    .sort((a, b) => b.value - a.value)
    .map((entry) => `${escapeChartMarkup(entry.name)}: [bold]${formatTooltipNumber(entry.value)}[/]`)
    .join('\n');
}

export function formatHeatmapHour(label: string, is12Hour: boolean, locale: string = DEFAULT_LABEL_LOCALE): string {
  const hour = Number(label);
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
    return label;
  }
  if (!is12Hour) {
    return `${String(hour).padStart(2, '0')}:00`;
  }
  return new Intl.DateTimeFormat(locale, { hour: 'numeric', hour12: true, timeZone: 'UTC' })
    .format(new Date(Date.UTC(2000, 0, 1, hour)))
    .replace(/\s/g, ' ');
}

export function formatHeatmapCategory(
  label: string,
  kind: AnalystHeatmapCategoryKind | undefined,
  is12Hour: boolean,
  translate: (key: string) => string,
  locale: string = DEFAULT_LABEL_LOCALE,
): string {
  switch (kind) {
    case AnalystHeatmapCategoryKind.HourOfDay:
      return formatHeatmapHour(label, is12Hour, locale);
    case AnalystHeatmapCategoryKind.DayOfWeek:
      return translate(label);
    default:
      return label;
  }
}

export function analystChartHostHeight(chart: AnalystChart): number {
  if (chart.heatmap) {
    return Math.max(ANALYST_CHART_BASE_HEIGHT, chart.heatmap.yCategories.length * HEATMAP_ROW_HEIGHT + HEATMAP_AXIS_ALLOWANCE);
  }
  return ANALYST_CHART_BASE_HEIGHT;
}

export function legendAwareHostHeight(legendHeight: number): number {
  const extraRows = Math.round((legendHeight - LEGEND_ROW_HEIGHT) / LEGEND_ROW_HEIGHT);
  const cappedRows = Math.min(Math.max(0, extraRows), LEGEND_MAX_ROWS - 1);
  return ANALYST_CHART_BASE_HEIGHT + cappedRows * LEGEND_ROW_HEIGHT;
}
